import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { CharacterReferenceCard } from "@/components/shared/card/CharacterReferenceCard";
import type {
  EmergencyContact,
  Dependent,
  CharacterReference,
  SaveFn,
} from "../../../lib/profileTypes";
import { SectionCard } from "../card/SectionCard";

interface ContactsTabProps {
  contacts: EmergencyContact[];
  dependents: Dependent[];
  characterRefs: CharacterReference[];
  candidateId: number;
  rawResume: string;
  canWrite: boolean;
  onSave: SaveFn;
  saving: boolean;
}

export function ContactsTab({
  contacts,
  dependents,
  characterRefs,
  candidateId,
  rawResume,
  canWrite,
  onSave,
  saving,
}: ContactsTabProps) {
  const queryClient = useQueryClient();
  const [ec, setEc] = useState(contacts);
  const [deps, setDeps] = useState(dependents);
  const [refs, setRefs] = useState(characterRefs);

  useEffect(() => {
    setRefs(characterRefs);
  }, [characterRefs]);
  useEffect(() => {
    setEc(contacts);
  }, [contacts]);
  useEffect(() => {
    setDeps(dependents);
  }, [dependents]);

  const intake = useMutation({
    mutationFn: () =>
      api.runIntakeAgent({ candidate_id: candidateId, resume_text: rawResume }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.candidateProfile(candidateId),
      }),
  });

  const inputCls =
    "rounded-btn border border-border-card bg-white px-2 py-1 text-xs";

  return (
    <div className="space-y-4">
      <SectionCard
        title="Character References"
        extra={
          rawResume ? (
            <button
              onClick={() => intake.mutate()}
              disabled={intake.isPending}
              title="Re-extract from resume"
              className="flex h-6 w-6 items-center justify-center rounded-btn text-text-secondary hover:bg-surface-secondary hover:text-near-black disabled:opacity-40"
            >
              <svg
                className={`h-3.5 w-3.5 ${intake.isPending ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.5 12a7.5 7.5 0 0 1 13.24-4.87L19.5 9M4.5 12H2m2.5 0H2m17.5 0a7.5 7.5 0 0 1-13.24 4.87L4.5 15M19.5 12H22m-2.5 0H22"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17.74 7.13 19.5 9l-2.09.65M6.26 16.87 4.5 15l2.09-.65"
                />
              </svg>
            </button>
          ) : null
        }
      >
        {intake.isPending && (
          <div className="flex items-center gap-2 py-2 text-xs text-amber-700">
            <svg
              className="h-3.5 w-3.5 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="3"
                opacity="0.25"
              />
              <path
                fill="currentColor"
                d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            Extracting character references from resume…
          </div>
        )}
        {!intake.isPending && refs.length === 0 && (
          <p className="py-2 text-xs text-text-secondary">
            No character references.
            {rawResume
              ? ' Click "Run intake agent" to auto-extract from the resume, or add manually.'
              : " Add manually below."}
          </p>
        )}
        <div className="space-y-3">
          {refs.map((r, i) => (
            <CharacterReferenceCard
              key={i}
              data={r}
              onChange={(updated) => {
                const n = [...refs];
                n[i] = updated;
                setRefs(n);
              }}
              onDelete={() => setRefs(refs.filter((_, j) => j !== i))}
            />
          ))}
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={() =>
              setRefs([
                ...refs,
                { name: "", position: "", company: "", phone: "", email: "" },
              ])
            }
            className="mt-3 text-xs font-medium text-rausch"
          >
            + Add reference
          </button>
        )}
      </SectionCard>

      <SectionCard title="Emergency Contacts">
        {ec.map((c, i) => (
          <div
            key={i}
            className="mb-2 grid grid-cols-2 gap-2 border-b border-surface-secondary pb-2 sm:grid-cols-4"
          >
            <input
              value={c.name}
              readOnly={!canWrite}
              onChange={(e) => {
                const n = [...ec];
                n[i] = { ...c, name: e.target.value };
                setEc(n);
              }}
              placeholder="Name"
              className={`${inputCls} read-only:opacity-60`}
            />
            <input
              value={c.relationship}
              readOnly={!canWrite}
              onChange={(e) => {
                const n = [...ec];
                n[i] = { ...c, relationship: e.target.value };
                setEc(n);
              }}
              placeholder="Relationship"
              className={`${inputCls} read-only:opacity-60`}
            />
            <input
              value={c.phone || ""}
              readOnly={!canWrite}
              onChange={(e) => {
                const n = [...ec];
                n[i] = { ...c, phone: e.target.value };
                setEc(n);
              }}
              placeholder="Phone"
              className={`${inputCls} read-only:opacity-60`}
            />
            <div className="flex gap-1">
              <input
                value={c.email || ""}
                readOnly={!canWrite}
                onChange={(e) => {
                  const n = [...ec];
                  n[i] = { ...c, email: e.target.value };
                  setEc(n);
                }}
                placeholder="Email"
                className={`flex-1 ${inputCls} read-only:opacity-60`}
              />
              {canWrite && (
                <button
                  onClick={() => setEc(ec.filter((_, j) => j !== i))}
                  className="text-xs text-error-text"
                >
                  x
                </button>
              )}
            </div>
          </div>
        ))}
        {canWrite && (
          <button
            type="button"
            onClick={() =>
              setEc([
                ...ec,
                { name: "", relationship: "", phone: "", email: "" },
              ])
            }
            className="text-xs font-medium text-rausch"
          >
            + Add contact
          </button>
        )}
      </SectionCard>

      <SectionCard title="Dependents">
        {deps.map((d, i) => (
          <div
            key={i}
            className="mb-2 grid grid-cols-2 gap-2 border-b border-surface-secondary pb-2 sm:grid-cols-4"
          >
            <input
              value={d.name}
              readOnly={!canWrite}
              onChange={(e) => {
                const n = [...deps];
                n[i] = { ...d, name: e.target.value };
                setDeps(n);
              }}
              placeholder="Name"
              className={`${inputCls} read-only:opacity-60`}
            />
            <input
              value={d.relationship}
              readOnly={!canWrite}
              onChange={(e) => {
                const n = [...deps];
                n[i] = { ...d, relationship: e.target.value };
                setDeps(n);
              }}
              placeholder="Relationship"
              className={`${inputCls} read-only:opacity-60`}
            />
            <input
              type="date"
              value={d.date_of_birth || ""}
              readOnly={!canWrite}
              onChange={(e) => {
                const n = [...deps];
                n[i] = { ...d, date_of_birth: e.target.value };
                setDeps(n);
              }}
              className={`${inputCls} read-only:opacity-60`}
            />
            <div className="flex gap-1">
              <input
                value={d.notes || ""}
                readOnly={!canWrite}
                onChange={(e) => {
                  const n = [...deps];
                  n[i] = { ...d, notes: e.target.value };
                  setDeps(n);
                }}
                placeholder="Notes"
                className={`flex-1 ${inputCls} read-only:opacity-60`}
              />
              {canWrite && (
                <button
                  onClick={() => setDeps(deps.filter((_, j) => j !== i))}
                  className="text-xs text-error-text"
                >
                  x
                </button>
              )}
            </div>
          </div>
        ))}
        {canWrite && (
          <button
            type="button"
            onClick={() => setDeps([...deps, { name: "", relationship: "" }])}
            className="text-xs font-medium text-rausch"
          >
            + Add dependent
          </button>
        )}
      </SectionCard>

      {canWrite && (
        <button
          onClick={() =>
            onSave({
              emergency_contacts: ec,
              dependents: deps,
              character_references: refs,
            })
          }
          disabled={saving}
          className="rounded-btn bg-near-black px-4 py-2 text-sm font-medium text-white hover:bg-rausch disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save"}
        </button>
      )}
    </div>
  );
}
